import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import appI18next from '../../../base/i18n/i18next';
import { getLogger } from '../../../base/logging/functions';
import BaseTheme from '../../../base/ui/components/BaseTheme.web';
import { withPixelLineHeight } from '../../../base/styles/functions.web';
const logger = getLogger('relay-entry');
// This bootstrap entry has no Redux/provider tree yet. Use the shared default
// theme for its loading/failure fallback; portal appearance remains separate.
// The standalone entry also has no BaseApp language provider.
const relayI18n = appI18next;
// Narrow the existing legacy style helper at this trusted token boundary.
const bodyTypography = withPixelLineHeight(BaseTheme.typography.bodyLongRegularLarge);
const useStyles = makeStyles()(() => ({
    shell: {
        ...bodyTypography,
        alignItems: 'center', background: BaseTheme.palette.uiBackground, boxSizing: 'border-box',
        color: BaseTheme.palette.text01, display: 'flex', justifyContent: 'center',
        minHeight: '100dvh', padding: BaseTheme.spacing(4)
    },
    content: { maxWidth: 440, textAlign: 'center' },
    message: { ...bodyTypography, margin: BaseTheme.spacing(0, 0, 3) },
    retry: {
        background: BaseTheme.palette.action01, border: '2px solid transparent',
        borderRadius: BaseTheme.shape.borderRadius, color: BaseTheme.palette.text01,
        cursor: 'pointer', display: 'block', font: 'inherit', margin: '0 auto', minHeight: 44, padding: BaseTheme.spacing(2, 4),
        '&:focus-visible': { outline: `3px solid ${BaseTheme.palette.focus01}`, outlineOffset: 4 }
    }
}));
/** Keep the standalone portal out of the initial download for conference pages. */
export default function LazyRelayPortal() {
    const { t } = useTranslation('main', { i18n: relayI18n });
    const { classes } = useStyles();
    const [Portal, setPortal] = useState(null);
    const [failed, setFailed] = useState(false);
    useEffect(() => {
        let active = true;
        import(/* webpackChunkName: "relay-portal" */ './RelayPortal.web').then(module => {
            if (active)
                setPortal(() => module.default);
        }).catch(error => {
            if (active) {
                logger.warn('Unable to load the Relay interface.', error);
                setFailed(true);
            }
        });
        return () => { active = false; };
    }, []);
    if (Portal)
        return React.createElement(Portal, null);
    return (React.createElement("div", { className: classes.shell },
        React.createElement("div", { className: classes.content },
            React.createElement("p", { className: classes.message, role: failed ? 'alert' : 'status' }, t(failed ? 'relayEntry.failed' : 'relayEntry.loading')),
            failed && React.createElement("button", { className: classes.retry, onClick: () => {
                    // Reload also recovers when an older page requests a retired chunk.
                    window.location.reload();
                }, type: 'button' }, t('relayEntry.retry')))));
}
